import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Rating,
  Chip,
  Avatar,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  CleaningServices as CleanIcon,
  Support as SupportIcon,
  Hotel as HotelIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  EmojiEvents as EmojiEventsIcon,
  SentimentSatisfied as SentimentIcon,
  SentimentVeryDissatisfied as DissatisfiedIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import Cookies from 'js-cookie';

const SECRET_KEY = 'tabtrips_hotel_secret';
const TOKEN_COOKIE_KEY = 'token';

function getDecryptedToken() {
  const encrypted = Cookies.get(TOKEN_COOKIE_KEY);
  if (!encrypted) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(encrypted, SECRET_KEY);
    return bytes.toString(CryptoJS.enc.Utf8);
  } catch {
    return null;
  }
}

const Performance = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedbackData, setFeedbackData] = useState([]);
  const [overallStats, setOverallStats] = useState({
    totalReviews: 0,
    averageRating: 0,
    cleanlinessAvg: 0,
    serviceAvg: 0,
    comfortAvg: 0,
    valueAvg: 0,
    recommendationRate: 0
  });

  useEffect(() => {
    fetchHotelFeedback();
  }, []);

  const fetchHotelFeedback = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = getDecryptedToken();
      if (!token) {
        setError('Authentication required. Please log in again.');
        return;
      }

      const hotelId = localStorage.getItem('selectedHotelId');
      if (!hotelId) {
        setError('No hotel selected. Please select a hotel first.');
        return;
      }

      const response = await axios.post(
        'https://api.tabtrips.com/olivrweb/hotelapi/dashboardApi.php/getHotelFeedBacks',
        {
          hotel_id: parseInt(hotelId),
          token: token
        },
        {
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.data.status) {
        const feedbacks = response.data.data || [];
        setFeedbackData(feedbacks);
        if (feedbacks.length > 0) {
          calculateOverallStats(feedbacks);
        } else {
          // No feedback data available
          setFeedbackData([]);
          setOverallStats({
            totalReviews: 0,
            averageRating: 0,
            cleanlinessAvg: 0,
            serviceAvg: 0,
            comfortAvg: 0,
            valueAvg: 0,
            recommendationRate: 0
          });
        }
      } else {
        setError(response.data.message || 'Failed to fetch feedback data');
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      setError('Failed to load feedback data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateOverallStats = (feedbacks) => {
    if (!feedbacks.length) return;

    const total = feedbacks.length;
    const overallSum = feedbacks.reduce((sum, item) => sum + item.overall_rating, 0);
    const cleanlinessSum = feedbacks.reduce((sum, item) => sum + item.cleanliness_rating, 0);
    const serviceSum = feedbacks.reduce((sum, item) => sum + item.service_rating, 0);
    const comfortSum = feedbacks.reduce((sum, item) => sum + item.comfort_rating, 0);
    const valueSum = feedbacks.reduce((sum, item) => sum + item.value_rating, 0);
    const recommendations = feedbacks.filter(item => item.would_recommend === 1).length;

    setOverallStats({
      totalReviews: total,
      averageRating: (overallSum / total).toFixed(1),
      cleanlinessAvg: (cleanlinessSum / total).toFixed(1),
      serviceAvg: (serviceSum / total).toFixed(1),
      comfortAvg: (comfortSum / total).toFixed(1),
      valueAvg: (valueSum / total).toFixed(1),
      recommendationRate: Math.round((recommendations / total) * 100)
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return '#4CAF50'; // Green
    if (rating >= 4.0) return '#8BC34A'; // Light Green
    if (rating >= 3.5) return '#FFC107'; // Yellow
    if (rating >= 3.0) return '#FF9800'; // Orange
    return '#F44336'; // Red
  };

  const getRatingLabel = (rating) => {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 4.0) return 'Very Good';
    if (rating >= 3.5) return 'Good';
    if (rating >= 3.0) return 'Average';
    return 'Below Average';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderStatCard = (title, value, icon, color, subtitle = '') => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{
        height: { xs: '180px', sm: '200px', md: '220px' }, // Fixed height for consistency
        background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`,
        border: `1px solid ${color}20`,
        borderRadius: 3,
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${color}25`,
          borderColor: `${color}40`
        }
      }}>
        <CardContent sx={{ 
          p: { xs: 2, sm: 2.5, md: 3 }, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%'
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: { xs: 44, sm: 48, md: 52 },
            height: { xs: 44, sm: 48, md: 52 },
            backgroundColor: color,
            borderRadius: '50%',
            color: '#ffffff',
            mb: { xs: 1.5, sm: 2 },
            flexShrink: 0
          }}>
            {React.cloneElement(icon, { sx: { fontSize: { xs: 20, sm: 22, md: 24 } } })}
          </Box>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            fontWeight="bold" 
            sx={{ 
              color: color, 
              mb: { xs: 0.5, sm: 1 },
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              lineHeight: 1.2
            }}
          >
            {value}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'text.secondary', 
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              mb: subtitle ? { xs: 0.5, sm: 0.75 } : 0,
              textAlign: 'center'
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'text.secondary', 
                opacity: 0.8,
                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                textAlign: 'center'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderFeedbackCard = (feedback, index) => (
    <motion.div
      key={feedback.feedback_id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      <Card sx={{
        mb: 2,
        borderRadius: 3,
        border: '1px solid rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
          borderColor: 'rgba(255, 119, 0, 0.3)'
        }
      }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ 
                backgroundColor: getRatingColor(feedback.overall_rating),
                width: { xs: 32, sm: 40 },
                height: { xs: 32, sm: 40 }
              }}>
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight="600" sx={{ color: 'text.primary' }}>
                  Guest Review
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {formatDate(feedback.insert_time)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Rating 
                  value={feedback.overall_rating} 
                  readOnly 
                  size={isMobile ? "small" : "medium"}
                  sx={{ '& .MuiRating-iconFilled': { color: '#FFD700' } }}
                />
                <Typography variant="body2" fontWeight="600" sx={{ color: 'text.primary' }}>
                  {feedback.overall_rating}/5
                </Typography>
              </Box>
              <Chip 
                label={getRatingLabel(feedback.overall_rating)}
                size="small"
                sx={{
                  backgroundColor: `${getRatingColor(feedback.overall_rating)}15`,
                  color: getRatingColor(feedback.overall_rating),
                  fontWeight: 600,
                  fontSize: '0.7rem'
                }}
              />
            </Box>
          </Box>

          {/* Rating Breakdown */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                <CleanIcon sx={{ fontSize: 20, color: '#4CAF50', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Cleanliness
                </Typography>
                <Rating value={feedback.cleanliness_rating} readOnly size="small" />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                <SupportIcon sx={{ fontSize: 20, color: '#2196F3', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Service
                </Typography>
                <Rating value={feedback.service_rating} readOnly size="small" />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                <HotelIcon sx={{ fontSize: 20, color: '#9C27B0', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Comfort
                </Typography>
                <Rating value={feedback.comfort_rating} readOnly size="small" />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box sx={{ textAlign: 'center', p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 2 }}>
                <StarIcon sx={{ fontSize: 20, color: '#FF9800', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Value
                </Typography>
                <Rating value={feedback.value_rating} readOnly size="small" />
              </Box>
            </Grid>
          </Grid>

          {/* Review Text */}
          {feedback.review_text && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ 
                color: 'text.primary', 
                lineHeight: 1.6,
                fontStyle: 'italic'
              }}>
                "{feedback.review_text}"
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            pt: 1,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {feedback.would_recommend === 1 ? (
                <Chip 
                  icon={<ThumbUpIcon />} 
                  label="Would Recommend" 
                  size="small"
                  sx={{ backgroundColor: '#4CAF50', color: '#ffffff' }}
                />
              ) : (
                <Chip 
                  icon={<ThumbDownIcon />} 
                  label="Would Not Recommend" 
                  size="small"
                  sx={{ backgroundColor: '#F44336', color: '#ffffff' }}
                />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {feedback.feedback_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );

  const renderEmptyState = () => (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, delay: 0.2 }}
    >
      <Box sx={{
        textAlign: 'center',
        py: { xs: 6, sm: 8, md: 10 },
        px: { xs: 3, sm: 4, md: 6 },
        background: 'linear-gradient(135deg, rgba(255, 119, 0, 0.03) 0%, rgba(255, 119, 0, 0.01) 100%)',
        borderRadius: 4,
        border: '2px dashed rgba(255, 119, 0, 0.2)',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(255, 119, 0, 0.05) 0%, transparent 50%)',
          zIndex: 0
        }
      }}>
        {/* Main Icon */}
        <Box sx={{ position: 'relative', zIndex: 1, mb: 4 }}>
          <Box sx={{
            width: { xs: 80, sm: 100, md: 120 },
            height: { xs: 80, sm: 100, md: 120 },
            background: 'linear-gradient(135deg, rgba(255, 119, 0, 0.1) 0%, rgba(255, 119, 0, 0.05) 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid rgba(255, 119, 0, 0.2)',
            boxShadow: '0 8px 32px rgba(255, 119, 0, 0.15)',
            mx: 'auto',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '-8px',
              left: '-8px',
              right: '-8px',
              bottom: '-8px',
              background: 'linear-gradient(45deg, rgba(255, 119, 0, 0.1), rgba(255, 119, 0, 0.05))',
              borderRadius: '50%',
              zIndex: -1,
              animation: 'pulse 2s infinite'
            }
          }}>
            <EmojiEventsIcon sx={{ 
              fontSize: { xs: 40, sm: 50, md: 60 }, 
              color: 'rgba(255, 119, 0, 0.8)',
              filter: 'drop-shadow(0 2px 4px rgba(255, 119, 0, 0.3))'
            }} />
          </Box>
        </Box>
        
        {/* Main Title */}
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight="600" sx={{ 
          mb: 3, 
          color: 'text.primary',
          fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
          position: 'relative',
          zIndex: 1
        }}>
          No Reviews Yet
        </Typography>
        
        {/* Description */}
        <Typography variant="body1" sx={{ 
          color: 'text.secondary', 
          mb: 4,
          maxWidth: 600,
          mx: 'auto',
          lineHeight: 1.7,
          fontSize: { xs: '0.875rem', sm: '1rem' },
          position: 'relative',
          zIndex: 1
        }}>
          Your hotel doesn't have any guest reviews yet. This is completely normal for new properties or during quiet periods. Great reviews will start appearing as guests share their experiences!
        </Typography>
        
        {/* Action Chips */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.5, sm: 2 },
          flexWrap: 'wrap',
          mb: 4,
          position: 'relative',
          zIndex: 1
        }}>
          <Chip 
            icon={<StarIcon />} 
            label="Encourage guest reviews"
            variant="outlined"
            sx={{ 
              borderColor: 'rgba(255, 119, 0, 0.4)',
              color: 'rgba(255, 119, 0, 0.8)',
              backgroundColor: 'rgba(255, 119, 0, 0.05)',
              '& .MuiChip-icon': { color: 'rgba(255, 119, 0, 0.8)' },
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: 2,
              py: 1
            }}
          />
          <Chip 
            icon={<TrendingUpIcon />} 
            label="Focus on guest satisfaction"
            variant="outlined"
            sx={{ 
              borderColor: 'rgba(76, 175, 80, 0.4)',
              color: 'rgba(76, 175, 80, 0.8)',
              backgroundColor: 'rgba(76, 175, 80, 0.05)',
              '& .MuiChip-icon': { color: 'rgba(76, 175, 80, 0.8)' },
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: 2,
              py: 1
            }}
          />
        </Box>
        
        {/* Tips Section */}
        <Box sx={{
          backgroundColor: 'rgba(255, 119, 0, 0.05)',
          borderRadius: 3,
          p: { xs: 2.5, sm: 3, md: 4 },
          border: '1px solid rgba(255, 119, 0, 0.15)',
          maxWidth: 500,
          mx: 'auto',
          position: 'relative',
          zIndex: 1,
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 20px rgba(255, 119, 0, 0.1)'
        }}>
          <Typography variant="body2" sx={{ 
            color: 'text.secondary',
            fontSize: { xs: '0.8rem', sm: '0.875rem' },
            lineHeight: 1.6,
            fontStyle: 'italic',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}>
            <span style={{ fontSize: '1.2rem' }}>💡</span>
            <strong>Pro Tip:</strong> Excellent service and memorable experiences naturally lead to positive reviews. Keep up the great work!
          </Typography>
        </Box>
        
        {/* CSS Animation for pulse effect */}
        <style>
          {`
            @keyframes pulse {
              0% { transform: scale(1); opacity: 0.8; }
              50% { transform: scale(1.05); opacity: 0.4; }
              100% { transform: scale(1); opacity: 0.8; }
            }
          `}
        </style>
      </Box>
    </motion.div>
  );

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <CircularProgress size={60} sx={{ color: '#FF7700' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ 
        maxWidth: 1200, 
        mx: 'auto', 
        pb: { xs: 4, sm: 6 },
        px: { xs: 1.5, sm: 2, md: 3 }
      }}>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box sx={{ 
            backgroundColor: '#ffffff', 
            borderRadius: 3, 
            p: { xs: 1.5, sm: 2, md: 3 }, 
            mb: { xs: 2, sm: 3 },
            boxShadow: 2,
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 1.5, sm: 2 },
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: 36, sm: 44, md: 48 },
                height: { xs: 36, sm: 44, md: 48 },
                backgroundColor: '#FF7700',
                borderRadius: '50%',
                color: '#ffffff',
                boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)',
                flexShrink: 0
              }}>
                <EmojiEventsIcon sx={{ fontSize: { xs: 18, sm: 20, md: 24 } }} />
              </Box>
              <Box>
                <Typography 
                  variant={isMobile ? "h5" : "h4"} 
                  fontWeight={600} 
                  sx={{ 
                    color: '#1e293b',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                    mb: { xs: 0.5, sm: 0 }
                  }}
                >
                  Performance & Reviews
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#64748b',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  }}
                >
                  Track your hotel's performance and guest satisfaction
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Paper sx={{ 
            borderRadius: 3, 
            overflow: 'hidden',
            boxShadow: 2,
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}>
            <Box sx={{
              backgroundColor: 'rgba(255, 119, 0, 0.05)',
              p: { xs: 1.5, sm: 2 },
              borderBottom: '1px solid rgba(255, 119, 0, 0.1)',
              textAlign: 'center'
            }}>
              <Typography variant="body1" fontWeight="500" sx={{ color: 'text.secondary' }}>
                No reviews available
              </Typography>
            </Box>
            
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              {renderEmptyState()}
            </Box>
          </Paper>
        </motion.div>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      maxWidth: 1200, 
      mx: 'auto', 
      pb: { xs: 4, sm: 6 },
      px: { xs: 1.5, sm: 2, md: 3 }
    }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ 
          backgroundColor: '#ffffff', 
          borderRadius: 3, 
          p: { xs: 1.5, sm: 2, md: 3 }, 
          mb: { xs: 2, sm: 3 },
          boxShadow: 2,
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 1.5, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' }
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: { xs: 36, sm: 44, md: 48 },
              height: { xs: 36, sm: 44, md: 48 },
              backgroundColor: '#FF7700',
              borderRadius: '50%',
              color: '#ffffff',
              boxShadow: '0 4px 12px rgba(255, 119, 0, 0.3)',
              flexShrink: 0
            }}>
              <EmojiEventsIcon sx={{ fontSize: { xs: 18, sm: 20, md: 24 } }} />
            </Box>
            <Box>
              <Typography 
                variant={isMobile ? "h5" : "h4"} 
                fontWeight={600} 
                sx={{ 
                  color: '#1e293b',
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' },
                  mb: { xs: 0.5, sm: 0 }
                }}
              >
                Performance & Reviews
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#64748b',
                  fontSize: { xs: '0.875rem', sm: '1rem' }
                }}
              >
                Track your hotel's performance and guest satisfaction
              </Typography>
            </Box>
          </Box>
        </Box>
      </motion.div>

      {/* Overall Stats - Only show if there's data */}
      {feedbackData.length > 0 && overallStats.totalReviews > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={6} sm={6} md={3}>
              {renderStatCard(
                'Overall Rating',
                `${overallStats.averageRating}/5`,
                <StarIcon />,
                '#FFD700',
                getRatingLabel(overallStats.averageRating)
              )}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderStatCard(
                'Total Reviews',
                overallStats.totalReviews,
                <TrendingUpIcon />,
                '#4CAF50'
              )}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderStatCard(
                'Recommendation Rate',
                `${overallStats.recommendationRate}%`,
                <ThumbUpIcon />,
                '#2196F3'
              )}
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              {renderStatCard(
                'Cleanliness',
                `${overallStats.cleanlinessAvg}/5`,
                <CleanIcon />,
                '#4CAF50'
              )}
            </Grid>
          </Grid>
        </motion.div>
      ) : (
        // Show empty state when no data
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {renderEmptyState()}
        </motion.div>
      )}

      {/* Feedback Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <Paper sx={{ 
          borderRadius: 3, 
          overflow: 'hidden',
          boxShadow: 2,
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}>
          <Box sx={{
            backgroundColor: 'rgba(255, 119, 0, 0.05)',
            p: { xs: 1.5, sm: 2 },
            borderBottom: '1px solid rgba(255, 119, 0, 0.1)',
            textAlign: 'center'
          }}>
            <Typography variant="body1" fontWeight="500" sx={{ color: 'text.secondary' }}>
              {feedbackData.length > 0 
                ? `${feedbackData.length} review${feedbackData.length !== 1 ? 's' : ''} from your guests`
                : 'No reviews available'
              }
            </Typography>
          </Box>
          
          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {feedbackData.length > 0 ? (
              <AnimatePresence>
                {feedbackData.map((feedback, index) => renderFeedbackCard(feedback, index))}
              </AnimatePresence>
            ) : (
              // Don't show empty state here since it's already shown above
              <Box sx={{ 
                textAlign: 'center', 
                py: 4,
                color: 'text.secondary',
                fontSize: '0.875rem'
              }}>
                No guest reviews available yet
              </Box>
            )}
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default Performance;
